import { useState, useRef } from "react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "https://tribaltech-backend.onrender.com";

function formatMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(1);
}

export default function CleanSlateUpload() {
  const [files, setFiles]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]  = useState({});
  const [uploaded, setUploaded]  = useState([]);
  const [error, setError]        = useState("");
  const fileRef = useRef();
  const token   = localStorage.getItem("tribal_token");

  const addFiles = (newFiles) => {
    const valid = Array.from(newFiles).filter(
      f => f.type.startsWith("video/") || f.type.startsWith("image/")
    );
    setFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const uploadOne = async (file) => {
    const setP = (pct, status) =>
      setProgress(p => ({ ...p, [file.name]: { pct, status } }));

    setP(10, "preparing");
    const presignRes = await fetch(API + '/api/upload/presign', {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify({ filename: file.name }),
    });
    if (!presignRes.ok) throw new Error("Could not prepare upload");
    const { upload_url, storage_path } = await presignRes.json();

    setP(20, "uploading");
    const form = new FormData();
    form.append("file", file, file.name);

    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const uploadRes = await fetch(upload_url, {
      method: "POST",
      body: form,
      headers: { "authorization": "Bearer " + anonKey },
    });

    if (!uploadRes.ok) {
      const err = await uploadRes.text();
      throw new Error("Storage upload failed: " + uploadRes.status + " " + err);
    }

    setP(90, "registering");
    const confirmRes = await fetch(API + '/api/upload/confirm', {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify({ storage_path, original_file: file.name }),
    });
    if (!confirmRes.ok) {
      const err = await confirmRes.json().catch(() => ({}));
      throw new Error(err.error || "Failed to register upload");
    }

    setP(100, "done");
  };

  const uploadAll = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    setProgress({});
    const results = [];
    for (const file of files) {
      try {
        await uploadOne(file);
        results.push({ name: file.name, status: "success" });
      } catch (e) {
        setProgress(p => ({ ...p, [file.name]: { pct: 0, status: "error" } }));
        results.push({ name: file.name, status: "error", msg: e.message });
      }
    }
    setUploaded(results);
    setFiles([]);
    setUploading(false);
  };

  const S = {
    page: { minHeight: "100vh", background: "#06060a", color: "#f0ede8", fontFamily: "'DM Sans', sans-serif", padding: "40px 24px" },
    card: { background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "28px" },
  };

  const statusLabel = (prog) => {
    if (!prog) return null;
    const map = { preparing: "Preparing...", uploading: "Uploading...", registering: "Saving...", done: "✓ Done", error: "✕ Failed" };
    return map[prog.status] || prog.status;
  };

  const statusColor = (status) => {
    if (status === "done") return "#0D9488";
    if (status === "error") return "#f09595";
    return "rgba(240,237,232,0.4)";
  };

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        a { text-decoration: none; color: inherit; }
        .drop-zone { border: 1.5px dashed rgba(13,148,136,0.4); border-radius: 14px; padding: 48px 24px; text-align: center; cursor: pointer; transition: all 0.2s; background: rgba(13,148,136,0.03); }
        .drop-zone:hover { border-color: rgba(13,148,136,0.7); background: rgba(13,148,136,0.06); }
        .btn-primary { background: #0D9488; color: #fff; border: none; border-radius: 8px; padding: 12px 28px; font-size: 14px; font-weight: 500; cursor: pointer; font-family: inherit; transition: background 0.2s; }
        .btn-primary:hover { background: #0a7a70; }
        .btn-primary:disabled { background: rgba(13,148,136,0.3); cursor: not-allowed; }
        .prog-track { height: 3px; background: rgba(255,255,255,0.06); border-radius: 2px; margin-top: 6px; overflow: hidden; }
        .prog-fill { height: 100%; border-radius: 2px; transition: width 0.3s ease; }
      `}</style>

      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <Link to="/dashboard" style={{ fontSize: 13, color: "rgba(240,237,232,0.4)", display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 40 }}>
          ← Back to dashboard
        </Link>

        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#0D9488", letterSpacing: "0.08em", marginBottom: 10 }}>CLEAN SLATE</div>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, fontWeight: 400, marginBottom: 10 }}>Upload your content</h1>
          <p style={{ fontSize: 14, color: "rgba(240,237,232,0.5)", lineHeight: 1.7 }}>
            Upload the videos and photos you want removed from the internet. We generate a private fingerprint of each file — we never share or distribute your content. Scanning begins within 24 hours.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 32 }}>
          {[
            { icon: "◈", text: "Your files are never shared or published" },
            { icon: "◉", text: "Complete anonymity — your name stays private" },
            { icon: "◎", text: "Files deleted from our servers after processing" },
          ].map((r, i) => (
            <div key={i} style={{ background: "rgba(13,148,136,0.05)", border: "0.5px solid rgba(13,148,136,0.12)", borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 18, color: "#0D9488", marginBottom: 8 }}>{r.icon}</div>
              <div style={{ fontSize: 12, color: "rgba(240,237,232,0.5)", lineHeight: 1.5 }}>{r.text}</div>
            </div>
          ))}
        </div>

        {uploaded.length === 0 && (
          <div style={S.card}>
            <div
              className="drop-zone"
              onClick={() => fileRef.current.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            >
              <input ref={fileRef} type="file" accept="video/*,image/*" multiple style={{ display: "none" }} onChange={e => addFiles(e.target.files)} />
              <div style={{ fontSize: 28, color: "#0D9488", marginBottom: 14 }}>◈</div>
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>Drop files here or click to browse</div>
              <div style={{ fontSize: 13, color: "rgba(240,237,232,0.4)" }}>Videos and photos — MP4, MOV, JPG, PNG and more</div>
              <div style={{ fontSize: 12, color: "rgba(13,148,136,0.5)", marginTop: 8 }}>No file size limit — uploads go directly to secure storage</div>
            </div>

            {files.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: "rgba(240,237,232,0.4)", letterSpacing: "0.08em", marginBottom: 14 }}>
                  {files.length} FILE{files.length !== 1 ? "S" : ""} SELECTED
                </div>
                {files.map((f, i) => {
                  const prog = progress[f.name];
                  return (
                    <div key={i} style={{ padding: "10px 0", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                          <div style={{ fontSize: 12, color: "rgba(240,237,232,0.35)" }}>{formatMB(f.size)} MB</div>
                        </div>
                        {!uploading ? (
                          <button onClick={() => removeFile(i)} style={{ background: "none", border: "none", color: "rgba(240,237,232,0.3)", cursor: "pointer", fontSize: 16, padding: "4px 8px" }}>✕</button>
                        ) : (
                          <span style={{ fontSize: 12, color: statusColor(prog?.status), flexShrink: 0 }}>{statusLabel(prog)}</span>
                        )}
                      </div>
                      {uploading && prog && (
                        <div className="prog-track">
                          <div className="prog-fill" style={{ width: `${prog.pct}%`, background: prog.status === "error" ? "#E24B4A" : "#0D9488" }} />
                        </div>
                      )}
                    </div>
                  );
                })}

                {error && (
                  <div style={{ background: "rgba(226,75,74,0.08)", border: "0.5px solid rgba(226,75,74,0.2)", borderRadius: 8, padding: "12px 16px", marginTop: 16, fontSize: 13, color: "#f09595" }}>{error}</div>
                )}

                <button className="btn-primary" style={{ width: "100%", padding: "14px", marginTop: 20, fontSize: 15 }} onClick={uploadAll} disabled={uploading}>
                  {uploading ? "Uploading directly to secure storage..." : `Upload ${files.length} file${files.length !== 1 ? "s" : ""} for protection`}
                </button>
              </div>
            )}
          </div>
        )}

        {uploaded.length > 0 && (
          <div style={{ ...S.card, textAlign: "center", padding: "48px 40px" }}>
            <div style={{ fontSize: 36, color: "#0D9488", marginBottom: 16 }}>◉</div>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, fontWeight: 400, marginBottom: 10 }}>
              {uploaded.filter(u => u.status === "success").length} file{uploaded.filter(u => u.status === "success").length !== 1 ? "s" : ""} uploaded
            </h2>
            <p style={{ fontSize: 14, color: "rgba(240,237,232,0.5)", lineHeight: 1.7, maxWidth: 400, margin: "0 auto 28px" }}>
              We're generating fingerprints now. Scanning begins within 24 hours. You'll get a report when we find and remove copies.
            </p>
            <div style={{ marginBottom: 28 }}>
              {uploaded.map((u, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "0.5px solid rgba(255,255,255,0.06)", textAlign: "left" }}>
                  <div style={{ fontSize: 13, color: "rgba(240,237,232,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: 12 }}>{u.name}</div>
                  <div style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, flexShrink: 0, background: u.status === "success" ? "rgba(13,148,136,0.12)" : "rgba(226,75,74,0.1)", color: u.status === "success" ? "#0D9488" : "#f09595" }}>
                    {u.status === "success" ? "Protected" : u.msg || "Failed"}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="btn-primary" onClick={() => { setUploaded([]); setFiles([]); }}>Upload more files</button>
              <Link to="/dashboard" style={{ display: "inline-flex", alignItems: "center", padding: "12px 24px", borderRadius: 8, border: "0.5px solid rgba(255,255,255,0.15)", fontSize: 14, color: "rgba(240,237,232,0.6)" }}>Go to dashboard</Link>
            </div>
          </div>
        )}

        <p style={{ fontSize: 12, color: "rgba(240,237,232,0.25)", textAlign: "center", marginTop: 24, lineHeight: 1.6 }}>
          Your files upload directly to encrypted secure storage and are deleted within 24 hours of fingerprinting. We retain only the fingerprint — a mathematical signature that cannot be used to reconstruct your content.
        </p>
      </div>
    </div>
  );
}
